"use client";

import React from "react";
import { Leaf, Wheat, Sun, MapPin } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const features = [{
  title: "Fuel-Efficient Fleet",
  description: "Our modern trucks are equipped with low-emission engines and optimized routing software to minimize our carbon footprint on every move.",
  icon: Leaf
}, {
  title: "Reusable Crates",
  description: "We offer heavy-duty, recycled plastic crates for packing, eliminating the need for single-use cardboard boxes and reducing waste.",
  icon: Wheat
}, {
  title: "Carbon Offsetting",
  description: "For every mile we drive, we invest in certified reforestation projects to ensure your relocation is 100% carbon neutral.",
  icon: Sun
}];
export default function OurGreenInitiative() {
  const headerReveal = useRevealOnScroll({
    threshold: 0.1
  });
  const gridReveal = useRevealOnScroll({
    threshold: 0.1
  });
  const mapReveal = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="223616" id="our-green-initiative" className="dark bg-background py-16 md:py-24 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {}
        <div ref={headerReveal.ref} className={`text-center max-w-3xl mx-auto mb-16 transition-all duration-1000 ease-out ${headerReveal.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="text-4xl md:text-5xl font-default font-bold text-foreground mb-4 tracking-tight uppercase">
            Our Green Initiative
          </h2>
          <p className="text-lg text-muted-foreground font-default">
            At SHIFT, we believe moving forward shouldn't mean leaving a footprint behind. 
            We are redefining logistics with sustainable practices that protect our planet 
            while delivering your world safely to its new destination.
          </p>
        </div>

        {}
        <div ref={gridReveal.ref} className={`grid grid-cols-1 md:grid-cols-3 gap-8 mb-12 transition-all duration-1000 delay-200 ease-out ${gridReveal.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          {features.map((feature, i) => <Card key={i} data-index={i} className="border-border/20 shadow-lg hover:shadow-xl transition-all bg-card/40 backdrop-blur-xl rounded-lg">
              <CardHeader>
                <div className="size-12 rounded-lg bg-primary/20 flex items-center justify-center mb-4 text-primary">
                  <feature.icon className="size-6" />
                </div>
                <CardTitle className="text-xl font-default font-bold text-foreground uppercase tracking-tight">
                  {feature.title}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-base text-muted-foreground font-default">
                  {feature.description}
                </CardDescription>
              </CardContent>
            </Card>)}
        </div>

        {}
        <div ref={mapReveal.ref} className={`transition-all duration-1000 delay-400 ease-out ${mapReveal.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <Card className="overflow-hidden border-none shadow-2xl bg-secondary/50 backdrop-blur-md rounded-lg">
            <div className="grid grid-cols-1 lg:grid-cols-5 items-center">
              <div className="p-8 lg:p-12 lg:col-span-2 flex flex-col justify-center h-full">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-md bg-primary text-primary-foreground text-xs font-bold mb-6 w-fit uppercase tracking-wider">
                  <MapPin className="size-4" />
                  <span>Eco-Logistics Network</span>
                </div>
                <h3 className="text-2xl md:text-3xl font-default font-bold text-foreground mb-4 uppercase tracking-tight">
                  Sustainable Routes
                </h3>
                <p className="text-muted-foreground mb-6 font-default">
                  Our logistics engine calculates the most efficient paths across the region, 
                  drastically reducing idle time and fuel consumption. We prioritize 
                  low-emission zones and green corridors to ensure a cleaner move.
                </p>
                <ul className="space-y-3">
                  {["Optimized Urban Routing", "Electric Vehicle Charging Hubs", "Zero-Waste Logistics Centers"].map((mill, i) => <li key={i} data-index={i} className="flex items-center gap-3 text-sm text-foreground font-semibold font-default">
                      <div className="size-2 rounded-sm bg-primary" />
                      {mill}
                    </li>)}
                </ul>
              </div>
              <div className="h-64 lg:h-full lg:col-span-3 relative bg-muted">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1685917/img/eco_moving_logistics_map.png" alt="Map showing eco-friendly moving routes and logistics hubs" className="absolute inset-0 w-full h-full object-cover mix-blend-luminosity opacity-40" />
                {}
                <div className="absolute inset-0 bg-gradient-to-r from-secondary/90 to-transparent lg:from-secondary/95 lg:via-secondary/40 lg:to-transparent" />
              </div>
            </div>
          </Card>
        </div>
      </div>
    </section>;
}